(() => {
  const EXAMPLES = [
    ['Can you help me plan a day in Kyoto?', 'Of course! Start at Fushimi Inari in the morning, enjoy lunch at Nishiki Market, then explore the lanes of Higashiyama. Finish with an evening walk through Gion.'],
    ['What should I pack for my trip to Japan?', 'Bring comfortable walking shoes, your passport, a light rain jacket, a travel adapter and a power bank. Leave some space in your bag for souvenirs.'],
    ['Can you suggest a family day in Osaka?', 'Start with Osaka Castle Park, visit the aquarium in the afternoon, and finish with dinner in Dotonbori. Keep time between stops for a relaxed family day.']
  ];

  const STARTER = [
    ...EXAMPLES,
    ['Could you turn this into a relaxed seven-day itinerary?', 'Absolutely. Spend the first three days in Tokyo, two days in Kyoto and the final two in Osaka. Keep one main activity for each morning and leave the afternoons flexible. This gives you time to explore without rushing between cities.'],
    ['What would a typical day in Tokyo look like?', 'Begin with a quiet walk through a neighborhood park, then visit a museum or explore a shopping district. Take a leisurely lunch break and leave time for a cafe. In the evening, choose a nearby restaurant and enjoy a short walk before returning to your hotel.'],
    ['Can you add some food ideas?', 'Try a bowl of ramen, freshly made onigiri and a small selection of seasonal dishes. In Kyoto, look for tofu dishes and matcha desserts. In Osaka, share a few street-food snacks. Choose places close to your planned stops so meals fit naturally into the day.'],
    ['How can we keep the schedule flexible?', 'Group activities by neighborhood and choose one optional stop each day. If the weather changes or everyone needs a rest, skip that stop and take a longer break. Save indoor options for rainy afternoons and keep travel days especially light.'],
    ['Please finish with a simple checklist.', 'Before leaving: check your passport, save hotel details and download an offline map. Each morning: check the weather, charge your phone and fill a water bottle. Each evening: review the next day together and pick the activity everyone is most excited about.']
  ];

  const init = (root) => {
    if (!root || root.dataset.pfhPhysicalReady) return;
    const demo = root.querySelector('.physical-demo');
    const fresh = demo?.querySelector('.physical-fresh');
    const input = demo?.querySelector('.physical-input');
    const messages = demo?.querySelector('.physical-messages');
    const status = demo?.querySelector('.physical-status');
    const face = demo?.querySelector('.physical-knob-face');
    const knob = demo?.querySelector('.physical-knob');
    if (!demo || !fresh || !input || !messages || !status || !face || !knob) return;
    root.dataset.pfhPhysicalReady = 'true';

    const resizeInput = () => {
      const screenHeight = fresh.clientHeight;
      input.style.height = '0px';
      const height = Math.min(screenHeight * 0.34, Math.max(screenHeight * 0.1, input.scrollHeight + 2));
      input.style.height = `${height}px`;
      input.style.overflowY = input.scrollHeight > height ? 'auto' : 'hidden';
      messages.style.bottom = `${screenHeight * 0.07 + height + 14}px`;
    };
    input.addEventListener('input', resizeInput);
    if (window.ResizeObserver) new ResizeObserver(resizeInput).observe(fresh);

    const column = demo.querySelector('.physical-device-column');
    const track = demo.querySelector('.physical-audio-track');
    const computer = demo.querySelector('.physical-computer');
    const alignTrack = () => {
      if (!column || !track || !computer) return;
      if (window.innerWidth <= 600) {
        column.style.paddingTop = '38px';
        track.style.top = '0px';
        return;
      }
      const top = Math.max(0, computer.offsetHeight * 0.084 - 4);
      track.style.top = `${top}px`;
      column.style.paddingTop = `${top + 40}px`;
    };
    if (window.ResizeObserver && computer) new ResizeObserver(alignTrack).observe(computer);
    alignTrack();

    demo.querySelector('.physical-screen')?.addEventListener('wheel', (event) => event.preventDefault(), { passive: false });
    messages.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false });

    let angle = 0;
    let typing = 0;
    let responding = 0;
    let sampleIndex = 0;
    let demoVisible = false;
    let voiceUsed = false;
    let reply = EXAMPLES[0][1];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    STARTER.forEach(([prompt, response]) => {
      const user = document.createElement('p');
      user.className = 'physical-message physical-message--user';
      user.textContent = prompt;
      const answer = document.createElement('p');
      answer.className = 'physical-message physical-message--answer';
      answer.textContent = response;
      messages.append(user, answer);
    });
    fresh.classList.add('has-messages');

    const startHints = () => {
      demo.classList.toggle('hint-voice-first', demoVisible && !voiceUsed);
      if (demoVisible && !voiceUsed) status.textContent = 'Press the Voice Key to start.';
    };

    if (window.IntersectionObserver) {
      const demoObserver = new IntersectionObserver((entries) => {
        const visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
        if (visible === demoVisible) return;
        demoVisible = visible;
        face.classList.remove('knob-entry-moving');
        if (demoVisible) {
          startHints();
          if (!reducedMotion.matches) {
            void face.offsetWidth;
            face.classList.add('knob-entry-moving');
          }
        } else {
          demo.classList.remove('hint-voice-first');
        }
      }, { threshold: 0.15 });
      demoObserver.observe(demo);
    }

    root.addEventListener('pointerenter', () => {
      voiceUsed = false;
      startHints();
    });

    const animateKey = (button) => {
      button.classList.remove('is-pressed');
      void button.offsetWidth;
      button.classList.add('is-pressed');
      window.setTimeout(() => button.classList.remove('is-pressed'), 220);
    };

    let targetAngle = 0;
    let motionFrame = 0;
    const dampRotation = () => {
      const delta = (targetAngle - angle) * 0.18;
      angle += delta;
      face.style.transform = `rotate(${angle}deg)`;
      messages.scrollTop += (delta * Math.PI) / 180 * messages.clientHeight * 0.85;
      if (Math.abs(targetAngle - angle) > 0.05) motionFrame = requestAnimationFrame(dampRotation);
      else {
        angle = targetAngle;
        motionFrame = 0;
      }
    };
    const moveKnob = (degrees) => {
      demo.querySelector('.knob-center').classList.remove('is-pressed');
      if (!motionFrame) targetAngle = angle;
      targetAngle += degrees;
      if (!motionFrame) motionFrame = requestAnimationFrame(dampRotation);
    };
    const rotate = (direction) => {
      face.classList.remove('knob-entry-moving');
      moveKnob(direction * 24);
      status.textContent = direction < 0 ? 'Turn left · Scroll up' : 'Turn right · Scroll down';
    };

    const reset = () => {
      cancelAnimationFrame(motionFrame);
      motionFrame = 0;
      targetAngle = angle;
      face.classList.remove('knob-entry-moving');
      clearInterval(typing);
      clearInterval(responding);
      messages.replaceChildren();
      input.value = '';
      resizeInput();
      demo.classList.remove('is-typing');
      fresh.classList.remove('has-messages');
      messages.scrollTop = 0;
      fresh.classList.remove('is-new-chat');
      void fresh.offsetWidth;
      fresh.classList.add('is-new-chat');
      demo.querySelector('.physical-key--voice').classList.remove('is-pressed');
      status.textContent = 'New conversation · Press the Voice Key to begin.';
      animateKey(demo.querySelector('.knob-center'));
      voiceUsed = false;
      demo.classList.toggle('hint-voice-first', demoVisible);
    };

    const voice = () => {
      if (demo.classList.contains('is-typing')) return;
      voiceUsed = true;
      demo.classList.remove('hint-voice-first');
      clearInterval(typing);
      const example = EXAMPLES[sampleIndex++ % EXAMPLES.length];
      reply = example[1];
      input.value = '';
      const started = performance.now();
      const continuation = ' Please include a relaxed schedule, practical suggestions and enough time for breaks.';
      let transcript = example[0] + continuation;
      demo.classList.add('is-typing');
      demo.querySelector('.physical-key--voice').classList.add('is-pressed');
      status.textContent = 'Voice input…';
      typing = window.setInterval(() => {
        const length = Math.floor((performance.now() - started) / 30);
        while (transcript.length < length) transcript += continuation;
        input.value = transcript.slice(0, length);
        resizeInput();
        input.scrollTop = input.scrollHeight;
      }, 32);
    };

    const stopVoice = () => {
      if (!demo.classList.contains('is-typing')) return;
      clearInterval(typing);
      demo.classList.remove('is-typing');
      demo.querySelector('.physical-key--voice').classList.remove('is-pressed');
      status.textContent = 'Press Confirm to send, or Cancel to clear.';
    };

    const send = () => {
      const value = input.value.trim();
      if (!value) {
        status.textContent = 'Press the Voice Key or type a message first.';
        return;
      }
      clearInterval(typing);
      clearInterval(responding);
      demo.classList.remove('is-typing');
      fresh.classList.add('has-messages');
      const user = document.createElement('p');
      user.className = 'physical-message physical-message--user';
      user.textContent = value;
      const answer = document.createElement('p');
      answer.className = 'physical-message physical-message--answer';
      messages.append(user, answer);
      input.value = '';
      resizeInput();
      let index = 0;
      status.textContent = 'Message sent · AI is responding…';
      responding = window.setInterval(() => {
        answer.textContent = reply.slice(0, ++index);
        messages.scrollTop = messages.scrollHeight;
        if (index >= reply.length) {
          clearInterval(responding);
          status.textContent = 'Reply received · Try another prompt.';
        }
      }, 15);
    };

    const clearInput = () => {
      clearInterval(typing);
      input.value = '';
      resizeInput();
      demo.classList.remove('is-typing');
      status.textContent = 'Input cleared.';
    };

    demo.querySelector('.knob-left').addEventListener('click', () => rotate(-1));
    demo.querySelector('.knob-right').addEventListener('click', () => rotate(1));
    demo.querySelector('.knob-center').addEventListener('click', reset);

    const voiceKey = demo.querySelector('.physical-key--voice');
    voiceKey.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      voiceKey.setPointerCapture(event.pointerId);
      voice();
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
      voiceKey.addEventListener(type, stopVoice);
    });
    voiceKey.addEventListener('keydown', (event) => {
      if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
        event.preventDefault();
        voice();
      }
    });
    voiceKey.addEventListener('keyup', (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        stopVoice();
      }
    });
    voiceKey.addEventListener('blur', stopVoice);

    [['confirm', send], ['cancel', clearInput]].forEach(([name, action]) => {
      const button = demo.querySelector(`.physical-key--${name}`);
      button.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        animateKey(button);
        action();
      });
      button.addEventListener('click', (event) => {
        if (event.detail === 0) {
          animateKey(button);
          action();
        }
      });
    });

    knob.addEventListener('wheel', (event) => {
      event.preventDefault();
      rotate(event.deltaY < 0 ? -1 : 1);
    }, { passive: false });
    knob.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        rotate(-1);
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        rotate(1);
      }
    });

    let drag = null;
    knob.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const box = knob.getBoundingClientRect();
      drag = {
        x: box.x + box.width / 2,
        y: box.y + box.height / 2,
        angle: Math.atan2(event.clientY - box.y - box.height / 2, event.clientX - box.x - box.width / 2),
        total: 0,
        moved: false
      };
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const next = Math.atan2(event.clientY - drag.y, event.clientX - drag.x);
      let delta = next - drag.angle;
      if (delta > Math.PI) delta -= Math.PI * 2;
      if (delta < -Math.PI) delta += Math.PI * 2;
      drag.angle = next;
      drag.total += delta;
      if (Math.abs(drag.total) > 0.035) drag.moved = true;
      if (!drag.moved) return;
      face.classList.remove('knob-entry-moving');
      face.style.transition = 'none';
      moveKnob((delta * 180) / Math.PI * 0.65);
      status.textContent = delta < 0 ? 'Turn left · Scroll up' : 'Turn right · Scroll down';
    });
    const endDrag = () => {
      face.style.transition = '';
      if (drag?.moved) {
        const suppress = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
        };
        knob.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => knob.removeEventListener('click', suppress, true), 100);
      }
      drag = null;
    };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
      }
      if (event.key === 'Escape') clearInput();
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-pfh-physical]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
